import React from "react";
import "../common/LicenseDetail.css";
import { useState, useRef, useEffect } from "react";
import Icon from "../../../common/Icon";
import cancelButton from "./../../../../assets/img/icon/cancleButton.svg";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import cloudUpload from "./../../../../assets/img/icon/cloudUpload.svg";
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import EditDetails from "../common/EditDetails";
import { useUpdateLicenseApplication } from "../../../../backend/license";
import { hasUserAccess, getUserDetails, getUserRole, } from "../../../../backend/auth";
import { toast } from "react-toastify";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../../shared/PageLoader";
import {
  useGetLicenseApplicationDocuments,
  useCreateLicenseApplicationDocument,
  useGetApplicationTypeDocuments,
  useGetLicenseApplicationHistory,
} from "../../../../backend/license";
import * as api from "../../../../backend/request";
import { useQueryClient } from "@tanstack/react-query";
import { downloadS3FileSecurely } from "../../../../utils/common";

interface LicenseDetailProps {
  isOpen: boolean;
  closePanel: () => void;
  license?: any;
  refetch?: () => void;
}

interface HistoryChange {
  from: any;
  to: any;
}

interface HistoryItem {
  id: number;
  action: string;
  changed_by: string;
  changed_at: string;
  changes: Record<string, HistoryChange>;
}

interface DisplayHistoryItem {
  id: number;
  title: string;
  date: string;
  rawDate: string;
  description: string;
  name: string;
}

type PopupData = {
  title: string;
  description: string;
  uploadBoxText?: string;
};

interface UploadedFile {
  file: File;
  url: string;
}

const LicenseApplicationDetail: React.FC<LicenseDetailProps> = ({
  isOpen,
  closePanel,
  license,
  refetch,
}) => {
  const [activeTab, setActiveTab] = useState<
    "details" | "history" | "documents"
  >("details");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [open, setIsOpen] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);
  // const [selectedStatus, setSelectedStatus] = useState<string>("Not started");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("not_started");
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [isPerpetual, setIsPerpetual] = useState<boolean>(false);
  const [issuedDate, setIssuedDate] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [lastUpdatedStatus, setLastUpdatedStatus] = useState<string | null>(null);
  const [allMandatoryDocsSubmitted, setAllMandatoryDocsSubmitted] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Removed uploadingDocuments state - files are now uploaded in batch on Submit click
  // const [uploadingDocuments, setUploadingDocuments] = useState<Record<string, number>>({});

  const user = getUserDetails();
  const queryClient = useQueryClient();

  const allStatuses = [
    "not_started",
    "docs_pending",
    "commercials_pending",
    "pending_at_authority",
    "application_approved",
    "application_rejected",
  ];

  const { updateApplication, updating } = useUpdateLicenseApplication(
    (response: any) => {
      toast.success("License application updated successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      setLastUpdatedStatus(selectedStatus);
      closePopup();
      closeEditPopup();
      closePanel();
      queryClient.invalidateQueries(['companyLicenses']);
      queryClient.invalidateQueries(['companyLicenseDocuments']);
      queryClient.invalidateQueries(['licenseApplications']);
      if (refetch) refetch();
    },
    (error: any) => {
      closePopup();
      closeEditPopup();
      closePanel();
      const errorData = error && error.response && error.response.data;
      const errors = errorData && errorData.errors;

      if (errors && Array.isArray(errors) && errors.length > 0) {
        errors.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message, {
          icon: <Icon name="snackbarError" />,
        });
      } else {
        toast.error("Failed to update license application", {
          icon: <Icon name="snackbarError" />,
        });
      }
    }
  );

  useEffect(() => {
    if (
      selectedStatus === "application_approved" &&
      issuedDate &&
      license &&
      license.license_type &&
      license.license_type.default_validity_days
    ) {
      const isPerpetualAllowed = license.license_type.perpetual_allowed;
      const applicationType = license.application_type && license.application_type.action_type;
      const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
      const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

      // Only auto-calculate for non-perpetual, non-surrender, non-amendment licenses
      if (!isPerpetualAllowed && !isSurrender && !isAmendment) {
        const issueDate = new Date(issuedDate);
        const validityDays = license.license_type.default_validity_days;
        const calculatedExpiry = new Date(issueDate);
        calculatedExpiry.setDate(calculatedExpiry.getDate() + validityDays);

        const formattedExpiry = calculatedExpiry.toISOString().split('T')[0];
        setExpiryDate(formattedExpiry);
      } else {
        setExpiryDate("");
      }
    }
  }, [
    issuedDate,
    selectedStatus,
    license && license.license_type && license.license_type.default_validity_days,
    license && license.license_type && license.license_type.perpetual_allowed,
    license && license.application_type && license.application_type.action_type
  ]);

  useEffect(() => {
    if (license && license.status) {
      setSelectedStatus(license.status);
      setLastUpdatedStatus(license.status);
    }
  }, [license]);


  useEffect(() => {
    if (lastUpdatedStatus !== null && selectedStatus === lastUpdatedStatus) {
      const remainingStatuses = allStatuses.filter(
        (s) => s !== lastUpdatedStatus
      );
      setSelectedStatus(lastUpdatedStatus);
    }
  }, [lastUpdatedStatus]);


  // Fetch required documents based on application type
  const {
    applicationTypeDocuments: requiredDocuments,
    isFetching: loadingRequiredDocs,
    refetch: _refetchRequiredDocs,
  } = useGetApplicationTypeDocuments(
    license && license.application_type_id ? license.application_type_id : undefined,
    license && license.application_type ? license.application_type.action_type : undefined,
    !!(license && license.application_type_id)
  );

  const {
    licenseApplicationDocuments: uploadedDocuments,
    isFetching: isDocumentsLoading,
    refetch: refetchUploadedDocs,
  } = useGetLicenseApplicationDocuments(
    license && license.id ? license.id : undefined,
    !!(license && license.id)
  );

  const {
    historyItems,
    isFetching: isLoadingHistory,
    refetch: refetchHistory,
  } = useGetLicenseApplicationHistory(
    license && license.id ? license.id : undefined,
    "watcher_ids,created_by,updated_by,editor_ids,assigned_to_id,escalation_user_id,status,description,license_name",
    !!(license && license.id)
  );

  const {
    createLicenseApplicationDocument,
    error: createError,
    creating: isCreating,
  } = useCreateLicenseApplicationDocument(
    (data: any) => {
      toast.success("Document uploaded successfully");
      refetchUploadedDocs();
    },
    (error: any) => {
      toast.error("Failed to upload document");
      console.error(error);
    }
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validateFile = (
    file: File,
    allowedTypes: string[],
    maxSizeBytes: number,
    documentName: string
  ): boolean => {

    const fileExtension = (file.name.split('.').pop() || '').toLowerCase();

    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      toast.error(
        `Invalid file type. Allowed formats: ${allowedTypes.join(', ').toUpperCase()}`
      );
      return false;
    }

    if (file.size > maxSizeBytes) {
      const maxSizeMB = (maxSizeBytes / 1024 / 1024).toFixed(2);
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      toast.error(
        `File size exceeds limit. Maximum: 25MB.`
      );
      return false;
    }
    return true;
  };

  const openPopup = (key: string) => {
    let popup = buttonPopups[key];

    if (key === 'application_approved' && license && license.application_type) {
      const actionType = license.application_type.action_type;

      popup = {
        ...popup,
        title: actionType === 'new' ? 'New License Application Approved' :
               actionType === 'renewal' ? 'Renewal Application Approved' :
               actionType === 'amendment' ? 'Amendment Application Approved' :
               actionType === 'surrender' ? 'Surrender Application Approved' :
               'Upload Confirmation',
        description: actionType === 'new' ?
          "Great! It seems that your application for new license is approved. Please enter the following information to update the license details." :
          actionType === 'renewal' ?
          "Great! It seems that your application for renewal is approved. Please enter the following information to update the license details. " :
          actionType === 'amendment' ?
          "Great! It seems that your application for amendment is approved. Please enter the following information to update the license details. " :
          actionType === 'surrender' ?
          "Great! It seems that your application for Surrender is approved. Please enter the following information to update the license details. " :
          "You have selected the status 'application approved', in order to proceed Please upload the license details and submit",
      };
    }

    if (popup) {
      setPopupData(popup);
      setIsOpen(true);
    } else {
      console.warn("No popup available for key:", key);
    }
  };

  const closePopup = () => {
    setIsOpen(false);
    setRejectionReason("");
    setIsPerpetual(false);
    setIssuedDate("");
    setExpiryDate("");
    setUploadedFile(null);
  };

  // Removed handleFileUpload - files are now stored locally and uploaded on Submit click

  const getUploadedDocument = (applicationTypeDocumentId: string) => {
    const doc = uploadedDocuments.find(
      (doc: any) =>
        doc.application_type_document_id === applicationTypeDocumentId
    );

    return doc;
  };

  const licenseDetails = license ? [
    { label: "ID", value: license.id || "-" },
    { label: "License Name", value: license.license_name || "-" },
    {
      label: "License Type",
      value: (license.license_type && license.license_type.name) || "-",
    },
    {
      label: "Application Type",
      value:
        license.application_type && license.application_type.action_type
          ? license.application_type.action_type
              .replace(/_/g, " ")
          : "-",
    },
    {
      label: "Company",
      value: (license.company && license.company.name) || "-",
    },
    {
      label: "Associate Company",
      value:
        (license.associate_company && license.associate_company.name) ||
        "-",
    },
    {
      label: "Location",
      value: license.location  ? `${license.location.name} - ${license.location.code}` : "-",
    },
    { label: "State", value: (license.state && license.state.name) || "-" },
    { label: "City", value: (license.city && license.city.name) || "-" },
    { label: "Authority", value: license.authority || "-" },
    { label: "Issued Date", value: license.issued_date || "-" },
    // { label: "Expiry Date", value: license.expiry_date || "-" },
    { label: "Expiry Date", value: license.license_type.perpetual_allowed ? "Perpetual License" : license.expiry_date || "-",},
    { label: "Assigned To", value: (license.assigned_to && license.assigned_to.name) || "-",
    },
    // {
    //   label: "Escalation User",
    //   value:
    //     (license.escalation_user && license.escalation_user.name) || "-",
    // },
    { label: "Watchers", value: license.watcher_names && license.watcher_names.length > 0  ? license.watcher_names.join(", ") : "No watchers",},
    { label: "Requested By", value: (license.requested_by && license.requested_by.name) || "-",},
    // { label: "License Status", value: license.status ? license.status.replace(/_/g, ' ').toUpperCase() : "Not started" },
  ] : [];

  const formatDateTime = (isoString: string | undefined | null) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatIssuedDateTime = (dateString?: string | null) => {
    if (!dateString) return "-";
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    let date = isDateOnly ? new Date(dateString + "T00:00:00") : new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour12: true,
    });
  };


  // Get the latest change with field updates from history API
  const formatFieldName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined) return "(empty)";
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') {
      // Handle empty strings
      if (value.trim() === '') return "(empty)";
      return value;
    }

    // Handle object with id and name (single object)
    if (typeof value === 'object' && value.id && value.name) {
      return value.name;
    }

    // Handle array of objects with id and name
    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value.map(item => {
        if (typeof item === 'object' && item.name) {
          return item.name;
        }
        return String(item);
      }).join(', ');
    }

    return String(value);
  };

  const displayHistoryItems: DisplayHistoryItem[] = [];
  const formatStatusText = (value: string | null | undefined) => {
    if (!value) return "-";
    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (license) {
    // Process audit logs (historyItems)
    if (historyItems && historyItems.length > 0) {
      historyItems.forEach((historyItem: HistoryItem, index: number) => {
        if (historyItem.changes && Object.keys(historyItem.changes).length > 0) {

          // Check if this audit log contains a status change
          if (historyItem.changes.status) {
            const statusChange = historyItem.changes.status;
            const fromStatus = formatStatusText(statusChange.from);
            const toStatus = formatStatusText(statusChange.to);
            const changedBy = historyItem.changed_by || '-';

            displayHistoryItems.push({
              id: 1000 + index,
              title: "Status changes",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `Status changed from <strong>${fromStatus}</strong> to <strong>${toStatus}</strong> by <strong style="color:#2965AD" class="text-capitalize">${changedBy}</strong>`,
              description: "",
            });
          }

          // Check for other field changes (non-status fields)
          const otherFields = Object.keys(historyItem.changes).filter(field => field !== 'status');

          if (otherFields.length > 0) {
            const changeTexts: string[] = [];

            otherFields.forEach((field: string) => {
              const change = historyItem.changes[field];
              const fieldLabel = formatFieldName(field);
              const fromValue = formatFieldValue(change.from);
              const toValue = formatFieldValue(change.to);
              changeTexts.push(
                `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong> by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || '-'}</strong>`
              );
            });

            const editedByText = changeTexts.length > 0 ? changeTexts.join('<br/>') : "-";

            displayHistoryItems.push({
              id: 2000 + index,
              title: "License Application Edited",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: editedByText,
              description: "",
            });
          }
        }
      });
    }

    // License Created On (always at the end chronologically, but will be sorted)
    displayHistoryItems.push({
      id: 3000,
      title: "License Created On",
      date: formatDateTime(license.created_at),
      rawDate: license.created_at,
      name: `Created By <strong style="color:#2965AD" class="text-capitalize">${license.requested_by && license.requested_by.name || "-"}</strong>`,
      description: "",
    });
  }

  // Sort by date (newest first)
  displayHistoryItems.sort((a, b) => {
    const dateA = new Date(a.rawDate || 0).getTime();
    const dateB = new Date(b.rawDate || 0).getTime();
    return dateB - dateA;
  });


  const buttonPopups: { [key: string]: PopupData } = {
    docs_pending: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Document under verification’ in order to proceed Please upload the Verification Successful confirmation message from team.",
    },
    commercials_pending: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Commercials Pending’, in order to proceed Please upload the Commercials Pending document from team.",
    },
    pending_at_authority: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Pending at authority’, in order to proceed Please upload the Verification Successful confirmation message from team.",
    },
    application_approved: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload License Copy",
      description: "You have selected the status ‘application approved’, in order to proceed Please upload the license details and submit",
    },
    application_rejected: {
      title: "Confirm Rejection",
      uploadBoxText: "Upload files",
      description: "Email will be sent to all the people with this status and you will no longer be able to make any edits to this application",
    },
  };

  const toggleAccordion = (id: number) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };

  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, UploadedFile | null>
  >({});

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Filter documents by action_type on frontend
  const filteredDocuments = requiredDocuments || [];

  const defaultAllowedFileTypes = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
    'pdf', 'ppt', 'pptx', 'doc', 'docx'
  ];

  const documentFields = filteredDocuments.map((doc: any) => ({
    key: doc.id,
    label: doc.document_type.name || "Unknown Document",
    applicationTypeDocumentId: doc.id,
    documentTypeCode: doc.document_type.code,
    allowedFileTypes: doc.document_type.allowed_file_types && doc.document_type.allowed_file_types.length > 0
      ? doc.document_type.allowed_file_types
      : defaultAllowedFileTypes,
    maxSizeBytes: 26214400,
    mandatory: doc.mandatory,
    documentTypeId: doc.document_type.id
  }));

  useEffect(() => {
    if (documentFields.length > 0) {
      setUploadedFiles((prev) => {
        const initialState: Record<string, UploadedFile | null> = {};
        documentFields.forEach((field: any) => {
          initialState[field.key] = prev[field.key] || null;
        });
        return initialState;
      });
    }
  }, [JSON.stringify(documentFields)]);

  const totalFiles = documentFields.length;
  // Count documents that are either locally selected OR already uploaded to backend
  const uploadedCount = documentFields.filter((docField: any) => {
    const hasLocalFile = uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined;
    const hasBackendFile = getUploadedDocument(docField.applicationTypeDocumentId);
    return hasLocalFile || hasBackendFile;
  }).length;
  const uploadProgress = totalFiles > 0 ? Math.round((uploadedCount / totalFiles) * 100) : 0;

  const checkMandatoryDocumentsSubmitted = () => {
    const missingMandatory = documentFields.filter((docField: any) => {
      if (!docField.mandatory) return false;
      const uploadedDoc = getUploadedDocument(docField.applicationTypeDocumentId);
      return !uploadedDoc;
    });
    return missingMandatory.length === 0;
  };
  useEffect(() => {
    const allSubmitted = checkMandatoryDocumentsSubmitted();
    setAllMandatoryDocsSubmitted(allSubmitted);
  }, [uploadedDocuments, documentFields]);

  const handleChooseFile = (key: string) => {
    const inputElement = fileInputRefs.current[key];
    if (inputElement) {
      inputElement.click();
    }
  };

  const handleFileSelect = (
    key: string,
    e: React.ChangeEvent<HTMLInputElement>,
    docField: any
  ) => {
    const files = e.target && e.target.files ? e.target.files : [];
    const file = files.length > 0 ? files[0] : null;
    if (file) {

      // Validate file
      const isValid = validateFile(
        file,
        docField.allowedFileTypes,
        docField.maxSizeBytes,
        docField.label
      );

      if (!isValid) {
        // Reset the input
        e.target.value = '';
        return;
      }

      const fileURL = URL.createObjectURL(file);
      setUploadedFiles((prev) => ({
        ...prev,
        [key]: { file, url: fileURL },
      }));

      // toast.success(`File uploaded successfully for ${docField.label}`);

      // File will be uploaded when Submit button is clicked
    }
  };

  const handleClosePanel = () => {
    setActiveTab("details");
    setLastUpdatedStatus(null);
    closePanel();
  };

  const handleRemoveFile = (key: string) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const handleDownload = (fileData: UploadedFile) => {
    const link = document.createElement("a");
    link.href = fileData.url;
    link.download = fileData.file.name;
    link.click();
  };

  // Batch submit all selected files in a single API call
  const handleBatchSubmit = async () => {
    try {
      setIsSubmitting(true);
      // Collect all selected files
      const selectedFiles = documentFields.filter((docField: any) =>
        uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined
      );

      if (selectedFiles.length === 0) {
        toast.error(`Please upload all mandatory documents`);
        return;
      }

      // Check mandatory documents - check both local state AND backend
      const missingMandatory = documentFields.filter((docField: any) => {
        if (!docField.mandatory) return false;
        const hasLocalFile = uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined;
        const hasBackendFile = getUploadedDocument(docField.applicationTypeDocumentId);
        return !hasLocalFile && !hasBackendFile;
      });

      if (missingMandatory.length > 0) {
        toast.error(`Please upload all mandatory documents`);
        return;
      }
      const formData = new FormData();
      selectedFiles.forEach((docField: any, index: number) => {
      const fileData = uploadedFiles[docField.key];
      if (fileData && fileData.file) {
        formData.append(
          `license_application_document[${index}][file]`,
          fileData.file
        );
        formData.append(
          `license_application_document[${index}][application_type_document_id]`,
          docField.applicationTypeDocumentId
        );
        formData.append(
          `license_application_document[${index}][license_application_id]`,
          license.id || ""
        );
      }
    })

      const endpoint = `/api/v1/license/license_application_documents`;
      toast.info("Uploading documents...");

      const response = await api.post(
        endpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(`Documents submitted successfully`, {
        icon: <Icon name="snackbarSuccess" />,
      });

      setUploadedFiles({});
      refetchUploadedDocs();
      closePanel();

    } catch (error: any) {
      console.error("Batch upload error:", error);
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      // Refetch to get any documents that may have been uploaded before the error
      refetchUploadedDocs();

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("File upload failed, try again");
      }

      // Clear local state after refetch is triggered so backend state is updated
      setTimeout(() => {
        setUploadedFiles({});
      }, 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const handleSave = (data: any, section: string) => {
    if (!license || !license.id) return;

    const payload = {
      id: license.id,
      license_application: {
        ...data,
        license_name: license.license_name,
        updated_by_id: user.user_id,
      },
    };

    updateApplication(payload as any);
  };

  const handleStatusUpdate = () => {
    if (!license || !license.id) {
      toast.error("License information is missing");
      return;
    }

    if (selectedStatus === "application_rejected") {
      if (!rejectionReason.trim()) {
        toast.error("Please provide a rejection reason");
        return;
      }
    }

    if (selectedStatus === "application_approved") {
      // if (!issuedDate) {
      //   toast.error("Please provide issued date");
      //   return;
      // }

      const licenseType = license.license_type;
      const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
      const applicationType = license.application_type && license.application_type.action_type;
      const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
      const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

      if (isSurrender || isAmendment) {
        const referenceIssueDate =
          (license.license_reference && license.license_reference.issued_date) ||
          (license.company_license && license.company_license.issued_date) ||
          license.issued_date;

        // if (!referenceIssueDate) {
        //   toast.error("Cannot approve: Issued date not found in the original license.");
        //   return;
        // }

        if (!isPerpetualAllowed) {
          const referenceExpiryDate =
            (license.license_reference && license.license_reference.expiry_date) ||
            (license.company_license && license.company_license.expiry_date) ||
            license.expiry_date;

          if (!referenceExpiryDate) {
            toast.error("Cannot approve: Expiry date not found in the original license.");
            return;
          }
        }
      } else {
        if (!issuedDate) {
          toast.error("Please provide issued date");
          return;
        }

        if (!isPerpetualAllowed && !expiryDate) {
          toast.error("Expiry date could not be calculated. Please ensure issued date is provided.");
          return;
        }
        if (!isPerpetualAllowed && expiryDate) {
          const expiryDateObj = new Date(expiryDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (expiryDateObj < today) {
            toast.error("Cannot issue license: The calculated expiry date is in the past. Please check the issued date.");
            return;
          }
        }
      }

      if (!uploadedFile) {
        toast.error("Please upload license file");
        return;
      }
    }

    if (["docs_pending", "commercials_pending", "pending_at_authority"].includes(selectedStatus)) {
      if (!uploadedFile) {
        toast.warning("Uploading a document is recommended for this status");
      }
    }

    const needsFile = uploadedFile !== null;

    let payload: any;

    if (needsFile) {
      const formData = new FormData();

      formData.append('license_application[status]', selectedStatus);
      formData.append('license_application[updated_by_id]', user.user_id);

      if (selectedStatus === "application_rejected") {
        formData.append('license_application[rejection_reason]', rejectionReason);
      }

      if (selectedStatus === "application_approved") {
        const licenseType = license.license_type;
        const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
        const applicationType = license.application_type && license.application_type.action_type;
        const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
        const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

        if (isSurrender || isAmendment) {
          const referenceIssueDate =
            (license.license_reference && license.license_reference.issued_date) ||
            (license.company_license && license.company_license.issued_date) ||
            license.issued_date;
          formData.append('license_application[issued_date]', referenceIssueDate);
          if (!isPerpetualAllowed) {
            const referenceExpiryDate =
              (license.license_reference && license.license_reference.expiry_date) ||
              (license.company_license && license.company_license.expiry_date) ||
              license.expiry_date;
            formData.append('license_application[expiry_date]', referenceExpiryDate);
          }
        } else {
          formData.append('license_application[issued_date]', issuedDate);

          if (!isPerpetualAllowed && expiryDate) {
            formData.append('license_application[expiry_date]', expiryDate);
          }
        }
      }

      formData.append('license_application[license_file]', uploadedFile);

      payload = {
        id: license.id,
        formData: formData,
        isFileUpload: true
      };

    } else {
      payload = {
        id: license.id,
        license_application: {
          status: selectedStatus,
          updated_by_id: user.user_id,
        },
      };

      if (selectedStatus === "application_rejected") {
        payload.license_application.rejection_reason = rejectionReason;
      }

      if (selectedStatus === "application_approved") {
        const licenseType = license.license_type;
        const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
        const applicationType = license.application_type && license.application_type.action_type;
        const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
        const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

        if (isSurrender || isAmendment) {
          const referenceIssueDate =
            (license.license_reference && license.license_reference.issued_date) ||
            (license.company_license && license.company_license.issued_date) ||
            license.issued_date;

          payload.license_application.issued_date = referenceIssueDate;

        if (!isPerpetualAllowed) {
            const referenceExpiryDate =
              (license.license_reference && license.license_reference.expiry_date) ||
              (license.company_license && license.company_license.expiry_date) ||
              license.expiry_date;

            payload.license_application.expiry_date = referenceExpiryDate;
          }
        } else {
          payload.license_application.issued_date = issuedDate;
          if (!isPerpetualAllowed && expiryDate) {
            payload.license_application.expiry_date = expiryDate;
          }
        }
      }

    }

    updateApplication(payload);
  };

  const handleDirectStatusUpdate = () => {
    if (!license || !license.id) {
      toast.error("License information is missing");
      return;
    }

    const payload = {
      id: license.id,
      license_application: {
        status: selectedStatus,
        updated_by_id: user.user_id,
      },
    };

    updateApplication(payload);
  };

  const getStatusColorClass = (status:any) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("not_started")) return "not_started";
    if (statusLower.includes("docs_pending")) return "renewal";
    if (statusLower.includes("rejected")) return "expired";
    if (statusLower.includes("expired")) return "expired";
    if (statusLower.includes("application_approved")) return "active";
    if (statusLower.includes("pending_at_authority")) return "renewal";
    if (statusLower.includes("commercials_pending")) return "renewal";
    return "";
  };

  return (
    <>
      {isOpen && (
        <div className="licensedetails-overlay" onClick={handleClosePanel}></div>
      )}
      <div className="licensedetails-wrapper">
        <div className={`licensedetails ${isOpen ? "open" : ""}`}>
          <div className="licensedetails-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Application Details</h5>
            <div className="d-flex justify-content-between align-items-center gap-4">
              {hasUserAccess(
                USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION
              ) && (
                <div
                  className={`d-flex align-items-center gap-2 ${
                    (license && license.status === 'application_approved') ||
                    (license && license.status === 'application_rejected')
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                  onClick={(e) => {
                    if (
                      (license && license.status === 'application_approved') ||
                      (license && license.status === 'application_rejected')
                    ) {
                      e.preventDefault();
                      return;
                    }
                    openEditPopup();
                  }}
                >
                  <img src={editIcon} alt="editIcon" />
                  <span className="edit_button">Edit</span>
                </div>
              )}
              <button
                className="btn-close"
                aria-label="Close"
                onClick={handleClosePanel}
              ></button>
            </div>
          </div>
          <div className="licensedetails-body h-100">
            <ul className="progress-section p-0 m-0 pl-2">
              <li
                className={activeTab === "details" ? "active" : ""}
                onClick={() => setActiveTab("details")}
              >
                Details
              </li>
              {(documentFields.length > 0) && (
              <li
                className={activeTab === "documents" ? "active" : ""}
                onClick={() => setActiveTab("documents")}
              >
                Documents
              </li>)}
              <li
                className={activeTab === "history" ? "active" : ""}
                onClick={() => setActiveTab("history")}
              >
                History
              </li>
            </ul>

            {/* License Details Tab */}
            {activeTab === "details" && (
              <ul className="licensedetails-text pb-1">
                {licenseDetails.map((item, index) => (
                  <li key={index}>
                    <div>{item.label}</div>
                    <div className="text-capitalize">{item.value}</div>
                  </li>
                ))}
                {license && license.application_type && license.application_type.sop && (
                  <li>
                    <div>
                      <label className="mb-2 fw-semibold">
                        Standard Operating Procedure for {license.application_type.action_type === 'new' ? 'New' :
                          license.application_type.action_type === 'renewal' ? 'Renewal' :
                          license.application_type.action_type === 'amendment' ? 'Amendment' :
                          license.application_type.action_type === 'surrender' ? 'Surrender' :
                          license.application_type.action_type}
                      </label>
                    </div>
                    <div className='w-100'>
                      <div className='w-100 licensetypedetails-textarea' style={{ whiteSpace: 'pre-wrap' }}>
                        {license.application_type.sop || 'No Standard Operating Procedure provided'}
                      </div>
                    </div>
                  </li>
                )}
                <li>
                  <div className="d-flex align-items-center">License Application Status</div>

                  {/* If user has access → show SELECT */}
                  {hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION) ? (
                    <div>
                      <select
                        className="licensedetails-select form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        style={{ width: "240px" }}
                        disabled={
                          (license && license.status === "application_approved") ||
                          (license && license.status === "application_rejected") ||
                          (license &&
                            license.status === "docs_pending" &&
                            !allMandatoryDocsSubmitted)
                        }
                      >
                        <option value="not_started" disabled={lastUpdatedStatus === "not_started"}>Not Started</option>
                        <option value="docs_pending" disabled={lastUpdatedStatus === "docs_pending"}>Docs Pending</option>
                        <option value="commercials_pending" disabled={lastUpdatedStatus === "commercials_pending"}>Commercials Pending</option>
                        <option value="pending_at_authority" disabled={lastUpdatedStatus === "pending_at_authority"}>Pending at Authority</option>
                        <option value="application_approved">Application Approved</option>
                        <option value="application_rejected">Application Rejected</option>
                      </select>

                      {license &&
                        license.status === "docs_pending" &&
                        !allMandatoryDocsSubmitted && (
                          <div className="text-muted small mt-1">
                            Please submit all the documents in the "Documents" tab before going ahead with this application. 
                          </div>
                        )}
                    </div>
                  ) : (
                    // If no access → show colored status text
                    <span
                      className={`text-capitalize license-tag color-class fw-bold ${getStatusColorClass(
                        license && license.status || ""
                      )}`}
                    >
                      {license && license.status.replace(/_/g, " ")}
                    </span>
                  )}
                </li>
              </ul>
            )}

            {activeTab === "documents" && (
              <>
                {(getUserRole() !== "License Watcher") && !((license && license.status === 'application_approved') || (license && license.status === 'application_rejected')) &&
                  <div className="progress-container">
                    <h2>{uploadProgress}%</h2>
                    <p>
                      {uploadProgress === 100
                        ? "All required documents have been uploaded"
                        : `${uploadedCount} of ${totalFiles} documents uploaded. Upload the remaining documents to proceed with the application`}
                    </p>
                    <div className="progress-bar">
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          background: "var(--royal-blue)",
                          height: "8px",
                          transition: "width 0.4s ease"
                        }}
                      ></div>
                    </div>
                  </div>
                }

                <div className="document-container pb-3 mt-4">
                  {(() => {
                    const isApprovedOrRejected =
                      (license && license.status === 'application_approved') ||
                      (license && license.status === 'application_rejected');
                    const isLicenseWatcher = getUserRole() === "License Watcher";

                    // Filter documents to show
                    const docsToShow = documentFields.filter((doc: any) => {
                      const uploadedDoc = getUploadedDocument(doc.applicationTypeDocumentId);

                      if (isLicenseWatcher && !uploadedDoc) {
                        return false;
                      }

                      if (isApprovedOrRejected && !uploadedDoc) {
                        return false;
                      }

                      return true;
                    });

                    // Show "No documents" message if no documents to display
                    if (docsToShow.length === 0) {
                      return (
                        <div className="text-center text-muted py-4">
                          No documents Submitted for this License Application
                        </div>
                      );
                    }

                    // Render documents
                    return docsToShow.map((doc: any) => {
                      const fileData = uploadedFiles[doc.key];
                      const uploadedDoc = getUploadedDocument(doc.applicationTypeDocumentId);
                      const isApprovedOrRejected =
                        (license && license.status === 'application_approved') ||
                        (license && license.status === 'application_rejected');

                      const acceptTypes = doc.allowedFileTypes.map((type: string) => `.${type}`).join(',');

                      return (
                        <div key={doc.key} className="mb-4">
                          <div className="d-flex align-items-center">
                            <label className="form-label ellipse two-lines">
                              {doc.label}
                              {doc.mandatory && <span className="text-danger ms-1">*</span>}
                            </label>

                            {!isApprovedOrRejected && !uploadedFiles[doc.key] && !uploadedDoc && (
                              <div
                                className="p-4 text-center upload-box w-100"
                                onClick={() => handleChooseFile(doc.key)}
                              >
                                <input
                                  ref={(el) => {
                                    fileInputRefs.current[doc.key] = el;
                                  }}
                                  type="file"
                                  accept={acceptTypes}
                                  onChange={(e) => handleFileSelect(doc.key, e, doc)}
                                  style={{ display: "none" }}
                                />
                                <div className="mb-2">
                                  <img src={cloudUpload} alt="cloudUpload" />
                                </div>
                                <p className="mb-2 text-md">
                                  Drag and drop a file here or click to browse
                                </p>
                                <small className="text-gray-12 d-block">
                                  Supported formats (Max {(doc.maxSizeBytes / 1024 / 1024).toFixed(0)}MB): {doc.allowedFileTypes.join(', ').toUpperCase()}
                                </small>
                              </div>
                            )}

                            {!isApprovedOrRejected && fileData && !uploadedDoc && (
                              <div className="upload-successful position-relative">
                                <div
                                  className="close-button position-absolute"
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(doc.key);
                                  }}
                                >
                                  <img
                                    className="d-block"
                                    src={cancelButton}
                                    alt="cancelButton"
                                  />
                                </div>

                                <div className="p-4 d-flex align-items-center justify-content-between">
                                  <p className="text-sm fw-semibold m-0 img_name_truncate">
                                    {fileData.file.name}
                                  </p>
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      height: "25px",
                                      width: "25px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(uploadedFiles[doc.key]!);
                                    }}
                                  >
                                    <img
                                      className="d-block"
                                      src={downloadButton}
                                      alt="downloadButton"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {uploadedDoc && (
                              <div className="upload-successful position-relative">
                                <div className="p-4 d-flex align-items-center justify-content-between">
                                  <p className="text-sm fw-semibold m-0 img_name_truncate">
                                    {uploadedDoc.asset.name || 'Uploaded Document'}
                                  </p>
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      height: "25px",
                                      width: "25px",
                                    }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (uploadedDoc.asset && uploadedDoc.asset.file_path) {
                                        try {
                                          const fileName = uploadedDoc.asset.name || uploadedDoc.asset.original_filename || 'document';
                                          await downloadS3FileSecurely(fileName, uploadedDoc.asset.file_path);
                                        } catch (error) {
                                          console.error("Error downloading file:", error);
                                          toast.error("Failed to download file");
                                        }
                                      }
                                    }}
                                  >
                                    <img
                                      className="d-block"
                                      src={downloadButton}
                                      alt="downloadButton"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}

            {/* History Tab */}
            {activeTab === "history" && (
              <>
                <div className="history-accordion">
                  {displayHistoryItems.map((item, index) => (
                    <div key={item.id} className="accordion-item">
                      <div className="accordion-timeline">
                        <div
                          className={`timeline-dot d-flex justify-content-center align-items-center ${
                            item ? "active" : ""
                          }`}
                        >
                          <div className="progress-checked"></div>{" "}
                          {/* add class "completed" when need to show checked */}
                        </div>
                        {index < displayHistoryItems.length - 1 && (
                          <div className="timeline-line">
                            <div
                              className={`dark-line ${
                                expandedId === item.id ? "expanded" : ""
                              }`}
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header"
                          // onClick={() => toggleAccordion(item.id)}
                        >
                          <div className="accordion-header-left">
                            <h6
                              className="accordion-title mb-0"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            ></h6>
                           <span className="accordion-name" dangerouslySetInnerHTML={{ __html: item.name }}></span>
                            <span className="accordion-date">{item.date}</span>
                          </div>
                          {/* <button className="accordion-toggle">
                            <Icon
                              name={
                                expandedId === item.id
                                  ? "chevronup"
                                  : "chevrondown"
                              }
                            />
                          </button> */}
                        </div>
                        <div
                          className={`accordion-body ${
                            expandedId === item.id ? "expanded" : "collapsed"
                          }`}
                        >
                          <div className="accordion-body-content">
                            {item.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {hasUserAccess(
            USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION
          ) &&
          <>
            {(activeTab === "details"
            ) && (
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  // className="button-primary w-100 fw-semibold"
                  className={`button-primary w-100 fw-semibold ${
                    (license && license.status === 'application_approved') ||
                    (license && license.status === 'application_rejected')
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                  disabled={
                    (license && license.status === 'application_approved') ||
                    (license && license.status === 'application_rejected')
                  }
                  onClick={() => {
                    if (selectedStatus === 'application_approved' || selectedStatus === 'application_rejected') {
                      openPopup(selectedStatus);
                    } 
                    // else if (selectedStatus === "not_started"){
                    //   return
                    // } 
                    else {
                      handleDirectStatusUpdate();
                    }
                  }}
                >
                  Update
                </button>
              </div>
            )}
            {activeTab === "documents" && (
              <div className="d-flex gap-3 w-100">
                {/* Only show Remove All button if there are locally selected files and status is not approved/rejected */}
                {!(license && (license.status === 'application_approved' || license.status === 'application_rejected')) &&
                  Object.keys(uploadedFiles).some(key => uploadedFiles[key] !== null && uploadedFiles[key] !== undefined) && (
                  <button
                    type="button"
                    className="button-secondary w-100 fw-semibold"
                    onClick={() => {
                      const fileCount = Object.keys(uploadedFiles).filter(key =>
                        uploadedFiles[key] !== null && uploadedFiles[key] !== undefined
                      ).length;

                      if (fileCount > 0) {
                        setUploadedFiles({});
                        toast.info(`${fileCount} selected file${fileCount !== 1 ? 's' : ''} cleared`);
                      }
                    }}
                  >
                    Remove All
                  </button>
                )}
                {/* Only show Submit button if status is not approved/rejected */}
                {!(license && (license.status === 'application_approved' || license.status === 'application_rejected')) && (
                  <button
                    type="button"
                    className="button-primary w-100 fw-semibold"
                    onClick={handleBatchSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                )}
              </div>
            )}
          </>}
        </div>

        {open && popupData && (
          <div className="license-popup-overlay">
            <div className="license-popup">
              <div className="popup-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-semibold fs-5 m-0">{popupData.title}</h6>
                  <p>{popupData.description}</p>
                </div>
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={closePopup}
                ></button>
              </div>
              <hr />
              {selectedStatus === "application_rejected" ? (
                <div className="popup-body fs-6">
                  <p className="text-sm mb-2">Reason*</p>
                  <textarea
                    name="reason"
                    id="reason"
                    placeholder="Write reason here"
                    className="form-control"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>
                </div>
              ) : (
                <PopupUploadSection
                  selectedStatus={selectedStatus}
                  popupData={popupData}
                  isPerpetual={isPerpetual}
                  setIsPerpetual={setIsPerpetual}
                  issuedDate={issuedDate}
                  setIssuedDate={setIssuedDate}
                  expiryDate={expiryDate}
                  setExpiryDate={setExpiryDate}
                  onFileSelect={setUploadedFile}
                  applicationType={license && license.application_type && license.application_type.action_type}
                  isPerpetualAllowed={license && license.license_type && license.license_type.perpetual_allowed}
                />
              )}
              <hr />
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className="button-secondary w-100 fw-semibold"
                  onClick={closePopup}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-primary w-100 fw-semibold"
                  onClick={handleStatusUpdate}
                  disabled={updating}
                >
                  {updating ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}
        {license && (
          <EditDetails
            isOpen={isEditOpen}
            onClose={closeEditPopup}
            activeSection="license-application"
            initialData={{
              assigned_to_user_id:
                license.assigned_to_user_id ||
                (license.assigned_to && license.assigned_to.id) ||
                "",
              watcher_ids: license.watcher_ids || [],
              company_id: license.company_id || "",
              associate_company_id: license.associate_company_id || "",
              location_id: license.location_id || (license.location && license.location.id) || "",
              license_name: license.license_name || "",
            }}
            onSave={handleSave}
            isLoading={updating}
          />
        )}
      </div>
      {updating && <PageLoader />}
    </>
  );
};

export default LicenseApplicationDetail;

const PopupUploadSection: React.FC<{
  selectedStatus: string;
  popupData: PopupData;
  isPerpetual: boolean;
  setIsPerpetual: (value: boolean) => void;
  issuedDate: string;
  setIssuedDate: (value: string) => void;
  expiryDate: string;
  setExpiryDate: (value: string) => void;
  onFileSelect: (file: File | null) => void;
  applicationType?: string;
  isPerpetualAllowed?: boolean;
}> = ({
  selectedStatus,
  popupData,
  isPerpetual,
  setIsPerpetual,
  issuedDate,
  setIssuedDate,
  expiryDate,
  setExpiryDate,
  onFileSelect,
  applicationType,
  isPerpetualAllowed,
}) => {
  const [popupFiles, setPopupFiles] = useState<
    Record<string, UploadedFile | null>
  >({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target && e.target.files ? e.target.files : [];
    const file = files.length > 0 ? files[0] : null;

    if (file) {
      const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'pdf', 'ppt', 'pptx', 'doc', 'docx'];
      const maxSizeBytes = 26214400; // 25MB
      const fileExtension = (file.name.split('.').pop() || '').toLowerCase();

      if (!fileExtension || !allowedTypes.includes(fileExtension)) {
        toast.error(
          `Invalid file type. Allowed formats: ${allowedTypes.join(', ').toUpperCase()}`
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      if (file.size > maxSizeBytes) {
        const maxSizeMB = (maxSizeBytes / 1024 / 1024).toFixed(2);
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(
          `File size exceeds limit. Maximum: 25MB.`
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const fileURL = URL.createObjectURL(file);
      setPopupFiles((prev) => ({
        ...prev,
        [selectedStatus]: { file, url: fileURL },
      }));
      onFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setPopupFiles((prev) => ({
      ...prev,
      [selectedStatus]: null,
    }));
    onFileSelect(null);
  };

  const currentFile = popupFiles[selectedStatus];

  return (
    <div className="popup-body fs-6">
      {selectedStatus === "application_approved" && (
        <div>
          {/* <div className="mb-3 d-flex align-items-center gap-5">
            <div>
              <label className="mb-0 me-auto text-md">Is Perpetual</label>
              <p className="mb-0 me-2 text-gray-12">
                Enable this toggle if this license does not expire
              </p>
            </div>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="isPerpetual"
                checked={isPerpetual}
                onChange={(e) => setIsPerpetual(e.target.checked)}
              />
            </div>
          </div> */}

          {applicationType && applicationType.toLowerCase() !== 'surrender' && applicationType.toLowerCase() !== 'amendment' && (
            <div className="mb-3">
              <label className="text-md">Issued On*</label>
              <input
                type="date"
                className="form-control text-md"
                placeholder="-/-"
                value={issuedDate}
                onChange={(e) => setIssuedDate(e.target.value)}
              />
            </div>
          )}

          {isPerpetualAllowed !== true && applicationType && applicationType.toLowerCase() !== 'surrender' && applicationType.toLowerCase() !== 'amendment' && (
            <div className="mb-3">
              <label className="text-md">Expiry Date</label>
              <input
                type="date"
                className="form-control text-md bg-light"
                placeholder="-/-"
                value={expiryDate}
                readOnly
                disabled
                style={{cursor: 'not-allowed'}}
              />
            </div>
          )}
        </div>
      )}

      <p className="text-md mb-2">{popupData.uploadBoxText}</p>

      {!currentFile ? (
        <div className="p-4 text-center upload-box w-100" onClick={handleChooseFile}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <div className="mb-2">
            <img src={cloudUpload} alt="cloudUpload" />
          </div>
          <p className="mb-2 text-md">
            Drag and drop a file here or click to browse
          </p>
          <button type="button" className="mb-2">
            Choose File
          </button>
          <div>
            <small className="text-gray-12 d-block">
              Supported formats (Upto 25 MB): JPG, PNG, WEBP, SVG, PDF, JPEG
            </small>
          </div>
        </div>
      ) : (
        <div className="upload-successful position-relative">
          <div
            className="close-button position-absolute"
            style={{ cursor: "pointer" }}
            onClick={handleRemoveFile}
          >
            <img className="d-block" src={cancelButton} alt="cancelButton" />
          </div>

          <div className="p-4 d-flex align-items-center justify-content-between">
            <p className="text-sm fw-semibold m-0 img_name_truncate">{currentFile.file.name}</p>
            <div
              style={{
                cursor: "pointer",
                height: "25px",
                width: "25px",
              }}
              onClick={() => {
                const link = document.createElement("a");
                link.href = currentFile.url;
                link.download = currentFile.file.name;
                link.click();
              }}
            >
              <img className="d-block" src={downloadButton} alt="downloadButton" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
